/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { customElement, html, internalProperty, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { findPeople, getEmailFromGraphEntity } from '../../graph/graph.people';
import { getPersonImage } from '../../graph/graph.photos';
import { getUserPresence } from '../../graph/graph.presence';
import { getUserWithPhoto } from '../../graph/graph.user';
import { Providers } from '../../Providers';
import { ProviderState } from '../../providers/IProvider';
import '../../styles/fabric-icon-font';
import { getSvg, SvgIcon } from '../../utils/SvgHelper';
import '../sub-components/mgt-flyout/mgt-flyout';
import { MgtTemplatedComponent } from '../templatedComponent';
import { PersonCardInteraction } from './../PersonCardInteraction';
import { styles } from './mgt-person-css';
export { PersonCardInteraction } from '../PersonCardInteraction';
/**
 * Enumeration to define what parts of the person component render
 *
 * @export
 * @enum {number}
 */
export var PersonViewType;
(function (PersonViewType) {
    /**
     * Render only the avatar
     */
    PersonViewType[PersonViewType["avatar"] = 2] = "avatar";
    /**
     * Render the avatar and one line of text
     */
    PersonViewType[PersonViewType["oneline"] = 3] = "oneline";
    /**
     * Render the avatar and two lines of text
     */
    PersonViewType[PersonViewType["twolines"] = 4] = "twolines";
})(PersonViewType || (PersonViewType = {}));
/**
 * The person component is used to display a person or contact by using their photo, name, and/or email address.
 *
 * @export
 * @class MgtPerson
 * @extends {MgtTemplatedComponent}
 *
 * @cssprop --avatar-size - {Length} Avatar size
 * @cssprop --avatar-border - {String} Avatar border
 * @cssprop --avatar-border-radius - {String} Avatar border radius
 * @cssprop --initials-color - {Color} Initials color
 * @cssprop --initials-background-color - {Color} Initials background color
 * @cssprop --font-family - {String} Font family
 * @cssprop --font-size - {Length} Font size
 * @cssprop --font-weight - {Length} Font weight
 * @cssprop --color - {Color} Color
 * @cssprop --text-transform - {String} text transform
 * @cssprop --line2-font-size - {Length} Line 2 font size
 * @cssprop --line2-font-weight - {Length} Line 2 font weight
 * @cssprop --line2-color - {Color} Line 2 color
 * @cssprop --line2-text-transform - {String} Line 2 text transform
 * @cssprop --details-spacing - {Length} spacing between avatar and person details
 */
let MgtPerson = class MgtPerson extends MgtTemplatedComponent {
    constructor() {
        super();
        // defaults
        this.personCardInteraction = PersonCardInteraction.none;
        this.line1Property = 'displayName';
        this.line2Property = 'email';
        this.view = PersonViewType.avatar;
        this.avatarSize = 'auto';
    }
    /**
     * Array of styles to apply to the element. The styles should be defined
     * user the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    /**
     * object containing Graph details on person
     * @type {IDynamicPerson}
     */
    get personDetails() {
        return this._personDetails;
    }
    set personDetails(value) {
        if (this._personDetails === value) {
            return;
        }
        this._personDetails = value;
        if (!value) {
            this._personAvatarBg = 'gray20';
        }
        else {
            this._personAvatarBg = this.getColorFromName(value.displayName);
        }
        this._fetchedImage = null;
        this._fetchedPresence = null;
        this.requestStateUpdate();
        this.requestUpdate('personDetails');
    }
    /**
     * Set the image of the person
     *
     * @type {string}
     * @memberof MgtPersonCard
     */
    get personImage() {
        return this._personImage || this._fetchedImage;
    }
    set personImage(value) {
        if (value === this._personImage) {
            return;
        }
        const oldValue = this._personImage;
        this._personImage = value;
        this.requestUpdate('personImage', oldValue);
    }
    /**
     * Gets or sets presence of person
     *
     * @type {MicrosoftGraphBeta.Presence}
     * @memberof MgtPerson
     */
    get personPresence() {
        return this._personPresence || this._fetchedPresence;
    }
    set personPresence(value) {
        if (value === this._personPresence) {
            return;
        }
        const oldValue = this._personPresence;
        this._personPresence = value;
        this.requestUpdate('personPresence', oldValue);
    }
    /**
     * Gets the flyout element
     *
     * @protected
     * @type {MgtFlyout}
     * @memberof MgtPerson
     */
    get flyout() {
        return this.renderRoot.querySelector('.flyout');
    }
    /**
     * Synchronizes property values when attributes change.
     *
     * @param {*} name
     * @param {*} oldValue
     * @param {*} newValue
     * @memberof MgtPerson
     */
    attributeChangedCallback(name, oldval, newval) {
        super.attributeChangedCallback(name, oldval, newval);
        if (oldval === newval) {
            return;
        }
        switch (name) {
            case 'person-query':
            case 'user-id':
                this.personDetails = null;
                this.requestStateUpdate();
                break;
        }
    }
    /**
     * Invoked on each update to perform rendering tasks. This method must return
     * a lit-html TemplateResult. Setting properties inside this method will *not*
     * trigger the element to update.
     */
    render() {
        // Loading
        if (this.isLoadingState && !this.personDetails) {
            return this.renderLoading();
        }
        // Prep data
        const person = this.personDetails;
        const image = this.getImage();
        const presence = this.personPresence || this._fetchedPresence;
        if (!person && !image) {
            return this.renderNoData();
        }
        // Default template
        let personTemplate = this.renderTemplate('default', { person, personImage: image });
        if (!personTemplate) {
            const detailsTemplate = this.renderDetails(person);
            const imageWithPresenceTemplate = this.renderAvatar(person, image, presence);
            personTemplate = html `
        <div class="person-root">
          ${imageWithPresenceTemplate} ${detailsTemplate}
        </div>
      `;
        }
        if (this.personCardInteraction !== PersonCardInteraction.none) {
            personTemplate = this.renderFlyout(personTemplate, person, image, presence);
        }
        return html `
      <div
        class="root"
        @click=${this.handleMouseClick}
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
      >
        ${personTemplate}
      </div>
    `;
    }
    /**
     * Render the loading state
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPerson
     */
    renderLoading() {
        return this.renderTemplate('loading', null) || html ``;
    }
    /**
     * Render the state when no data is available
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPerson
     */
    renderNoData() {
        const noDataTemplate = this.renderTemplate('no-data', null);
        if (noDataTemplate) {
            return noDataTemplate;
        }
        const avatarClasses = {
            'avatar-icon': true,
            'ms-Icon': true,
            'ms-Icon--Contact': true,
            small: !this.isLargeAvatar()
        };
        return html `
      <i class=${classMap(avatarClasses)}></i>
    `;
    }
    /**
     * Render the image part of the person template.
     * If the image is unavailable, the person's initials will be used instead.
     *
     * @protected
     * @param {string} [imageSrc]
     * @param {IDynamicPerson} [personDetails]
     * @returns
     * @memberof MgtPerson
     */
    renderImage(personDetails, imageSrc) {
        const title = personDetails && this.personCardInteraction === PersonCardInteraction.none ? personDetails.displayName : '';
        if (imageSrc) {
            return html `
        <img alt=${title} src=${imageSrc} />
      `;
        }
        else if (personDetails) {
            const initials = this.getInitials(personDetails);
            return html `
        <span class="initials-text" aria-label="${initials}">
          ${initials && initials.length
                ? html `
                ${initials}
              `
                : html `
                <i class="ms-Icon ms-Icon--Contact contact-icon"></i>
              `}
        </span>
      `;
        }
    }
    /**
     * Render presence for the person.
     *
     * @protected
     * @param
     * @memberof MgtPersonCard
     */
    renderPresence(presence) {
        if (!this.showPresence || !presence) {
            return html ``;
        }
        let statusClass = null;
        // attach appropriate css class to show different icons
        switch (presence.availability) {
            case 'DoNotDisturb':
                switch (presence.activity) {
                    case 'OutOfOffice':
                        statusClass = 'presence-oof-dnd';
                        break;
                    default:
                        statusClass = 'presence-dnd';
                        break;
                }
                break;
            case 'BeRightBack':
                statusClass = 'presence-away';
                break;
            case 'Available':
                switch (presence.activity) {
                    case 'Available':
                        statusClass = 'presence-available';
                        break;
                    case 'OutOfOffice':
                        statusClass = 'presence-oof-available';
                        break;
                }
                break;
            case 'Busy':
                switch (presence.activity) {
                    case 'OutOfOffice':
                        statusClass = 'presence-oof-busy';
                        break;
                    default:
                        // 'Busy', 'InACall', 'InAMeeting'
                        statusClass = 'presence-busy';
                        break;
                }
                break;
            case 'Away':
                switch (presence.activity) {
                    case 'Away':
                        statusClass = 'presence-away';
                        break;
                    case 'OutOfOffice':
                        statusClass = 'presence-oof-offline';
                        break;
                }
                break;
            case 'Offline':
                switch (presence.activity) {
                    case 'Offline':
                        statusClass = 'presence-offline';
                        break;
                    case 'OutOfOffice':
                        statusClass = 'presence-oof-offline';
                        break;
                }
                break;
            default:
                statusClass = 'presence-offline';
                break;
        }
        const presenceClasses = {
            'ms-Icon': true,
            'presence-basic': true
        };
        presenceClasses[statusClass] = true;
        // workaround because SkypeArrow icon from fluent doesn't work ¯\_(ツ)_/¯
        let iconHtml = null;
        if (statusClass === 'presence-oof-offline') {
            iconHtml = html `
        <div class="ms-Icon presence-basic presence-oof-offline-wrapper">
          <i class="presence-oof-offline">
            ${getSvg(SvgIcon.SkypeArrow, '#666666')}
          </i>
        </div>
      `;
        }
        else {
            iconHtml = html `
        <i class=${classMap(presenceClasses)} aria-hidden="true"></i>
      `;
        }
        return html `
      <div class="user-presence" title=${presence.activity} aria-label=${presence.activity}>
        ${iconHtml}
      </div>
    `;
    }
    /**
     * Render image with presence for the person.
     *
     * @protected
     * @param
     * @memberof MgtPersonCard
     */
    renderAvatar(personDetails, image, presence) {
        const title = this.personDetails && this.personCardInteraction === PersonCardInteraction.none
            ? this.personDetails.displayName
            : '';
        const imageClasses = {
            initials: !image,
            small: !this.isLargeAvatar(),
            'user-avatar': true
        };
        if (!image && this.personDetails) {
            // add avatar background color
            imageClasses[this._personAvatarBg] = true;
        }
        const imageTemplate = this.renderImage(personDetails, image);
        const presenceTemplate = this.renderPresence(presence);
        return html `
      <div class=${classMap(imageClasses)} title=${title} aria-label=${title}>
        ${imageTemplate} ${presenceTemplate}
      </div>
    `;
    }
    /**
     * Render the details part of the person template.
     *
     * @protected
     * @param {IDynamicPerson} [person]
     * @param {string} [image]
     * @returns {TemplateResult}
     * @memberof MgtPerson
     */
    renderDetails(person) {
        if (!person || (!this.showEmail && !this.showName && this.view === PersonViewType.avatar)) {
            return html ``;
        }
        const details = [];
        if (this.showName || this.view > PersonViewType.avatar) {
            const text = this.getTextFromProperty(person, this.line1Property);
            if (text) {
                details.push(html `
          <div class="line1" aria-label="${text}">${text}</div>
        `);
            }
        }
        if (this.showEmail || this.view > PersonViewType.oneline) {
            const text = this.getTextFromProperty(person, this.line2Property);
            if (text) {
                details.push(html `
          <div class="line2" aria-label="${text}">${text}</div>
        `);
            }
        }
        const detailsClasses = classMap({
            details: true,
            small: !this.isLargeAvatar()
        });
        return html `
      <div class="${detailsClasses}">
        ${details}
      </div>
    `;
    }
    /**
     * Render the details flyout.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPerson
     */
    renderFlyout(anchor, personDetails, image, presence) {
        const flyoutContent = this._personCardShouldRender
            ? html `
          <div slot="flyout">
            ${this.renderFlyoutContent(personDetails, image, presence)}
          </div>
        `
            : html ``;
        return html `
      <mgt-flyout light-dismiss class="flyout">
        ${anchor} ${flyoutContent}
      </mgt-flyout>
    `;
    }
    /**
     * Render the flyout menu content.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPerson
     */
    renderFlyoutContent(personDetails, image, presence) {
        return (this.renderTemplate('person-card', { person: personDetails, personImage: image }) ||
            html `
        <mgt-person-card
          .personDetails=${personDetails}
          .personImage=${image}
          .personPresence=${presence}
          .showPresence=${this.showPresence}
        ></mgt-person-card>
      `);
    }
    /**
     * load state into the component.
     *
     * @protected
     * @returns
     * @memberof MgtPerson
     */
    loadState() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = Providers.globalProvider;
            if (!provider || provider.state === ProviderState.Loading) {
                return;
            }
            if (provider.state === ProviderState.SignedOut) {
                this.personDetails = null;
                return;
            }
            const graph = provider.graph.forComponent(this);
            if (this.personDetails) {
                if (!this.personDetails.personImage &&
                    ((this.fetchImage && !this.personImage && !this._fetchedImage) || this.personImage === '@')) {
                    const image = yield getPersonImage(graph, this.personDetails);
                    if (image) {
                        this.personDetails.personImage = image;
                        this._fetchedImage = image;
                    }
                }
            }
            else if (this.userId || this.personQuery === 'me') {
                // Use userId or 'me' query to get the person and image
                const person = yield getUserWithPhoto(graph, this.userId);
                this.personDetails = person;
                this._fetchedImage = this.getImage();
            }
            else if (this.personQuery) {
                // Use the personQuery to find our person.
                const people = yield findPeople(graph, this.personQuery, 1);
                if (people && people.length) {
                    this.personDetails = people[0];
                    const image = yield getPersonImage(graph, people[0]);
                    if (image) {
                        this.personDetails.personImage = image;
                        this._fetchedImage = image;
                    }
                }
            }
            // populate presence
            const defaultPresence = {
                activity: 'Offline',
                availability: 'Offline',
                id: null
            };
            if (this.showPresence && !this.personPresence && !this._fetchedPresence) {
                try {
                    if (this.personDetails && this.personDetails.id) {
                        this._fetchedPresence = yield getUserPresence(graph, this.personDetails.id);
                    }
                    else {
                        this._fetchedPresence = defaultPresence;
                    }
                }
                catch (_) {
                    // set up a default Presence in case beta api changes or getting error code
                    this._fetchedPresence = defaultPresence;
                }
            }
            // populate avatar size
            if (this.showEmail && this.showName) {
                this.avatarSize = 'large';
            }
        });
    }
    getImage() {
        if (this.personImage && this.personImage !== '@') {
            return this.personImage;
        }
        if (this._fetchedImage) {
            return this._fetchedImage;
        }
        const person = this.personDetails;
        return person && person.personImage ? person.personImage : null;
    }
    getInitials(person) {
        if (!person) {
            person = this.personDetails;
        }
        if (person.initials) {
            return person.initials;
        }
        let initials = '';
        if (person.givenName) {
            initials += person.givenName[0].toUpperCase();
        }
        if (person.surname) {
            initials += person.surname[0].toUpperCase();
        }
        if (!initials && person.displayName) {
            const name = person.displayName.split(/\s+/);
            for (let i = 0; i < 2 && i < name.length; i++) {
                if (name[i][0] && this.isLetter(name[i][0])) {
                    initials += name[i][0].toUpperCase();
                }
            }
        }
        return initials;
    }
    isLetter(char) {
        try {
            return char.match(new RegExp('\\p{L}', 'u'));
        }
        catch (e) {
            return char.toLowerCase() !== char.toUpperCase();
        }
    }
    getTextFromProperty(personDetails, prop) {
        if (!property || property.length === 0) {
            return null;
        }
        const properties = prop.trim().split(',');
        let text;
        let i = 0;
        while (!text && i < properties.length) {
            const currentProp = properties[i].trim();
            switch (currentProp) {
                case 'mail':
                case 'email':
                    text = getEmailFromGraphEntity(personDetails);
                    break;
                default:
                    text = personDetails[currentProp];
            }
            i++;
        }
        return text;
    }
    isLargeAvatar() {
        return (this.avatarSize === 'large' ||
            (this.avatarSize === 'auto' && ((this.showEmail && this.showName) || this.view > PersonViewType.oneline)));
    }
    handleMouseClick(e) {
        if (this.personCardInteraction === PersonCardInteraction.click) {
            this.showPersonCard();
        }
    }
    handleMouseEnter(e) {
        clearTimeout(this._mouseEnterTimeout);
        clearTimeout(this._mouseLeaveTimeout);
        if (this.personCardInteraction !== PersonCardInteraction.hover) {
            return;
        }
        this._mouseEnterTimeout = setTimeout(this.showPersonCard.bind(this), 500);
    }
    handleMouseLeave(e) {
        clearTimeout(this._mouseEnterTimeout);
        clearTimeout(this._mouseLeaveTimeout);
        this._mouseLeaveTimeout = setTimeout(this.hidePersonCard.bind(this), 500);
    }
    hidePersonCard() {
        const flyout = this.flyout;
        if (flyout) {
            flyout.isOpen = false;
        }
        const personCard = (this.querySelector('mgt-person-card') ||
            this.renderRoot.querySelector('mgt-person-card'));
        if (personCard) {
            personCard.isExpanded = false;
        }
    }
    showPersonCard() {
        if (!this._personCardShouldRender) {
            this._personCardShouldRender = true;
        }
        const flyout = this.flyout;
        if (flyout) {
            flyout.isOpen = true;
        }
    }
    getColorFromName(name) {
        const charCodes = name
            .split('')
            .map(char => char.charCodeAt(0))
            .join('');
        const nameInt = parseInt(charCodes, 10);
        const colors = [
            'pinkRed10',
            'red20',
            'red10',
            'orange20',
            'orangeYellow20',
            'green10',
            'green20',
            'cyan20',
            'cyan30',
            'cyanBlue10',
            'cyanBlue20',
            'blue10',
            'blueMagenta30',
            'blueMagenta20',
            'magenta20',
            'magenta10',
            'magentaPink10',
            'orange30',
            'gray30',
            'gray20'
        ];
        return colors[nameInt % colors.length];
    }
};
__decorate([
    property({
        attribute: 'person-query'
    })
], MgtPerson.prototype, "personQuery", void 0);
__decorate([
    property({
        attribute: 'user-id'
    })
], MgtPerson.prototype, "userId", void 0);
__decorate([
    property({
        attribute: 'show-name',
        type: Boolean
    })
], MgtPerson.prototype, "showName", void 0);
__decorate([
    property({
        attribute: 'show-email',
        type: Boolean
    })
], MgtPerson.prototype, "showEmail", void 0);
__decorate([
    property({
        attribute: 'show-presence',
        type: Boolean
    })
], MgtPerson.prototype, "showPresence", void 0);
__decorate([
    property({
        attribute: 'avatar-size',
        type: String
    })
], MgtPerson.prototype, "avatarSize", void 0);
__decorate([
    property({
        attribute: 'person-details',
        type: Object
    })
], MgtPerson.prototype, "personDetails", null);
__decorate([
    property({
        attribute: 'person-image',
        type: String
    })
], MgtPerson.prototype, "personImage", null);
__decorate([
    property({
        attribute: 'fetch-image',
        type: Boolean
    })
], MgtPerson.prototype, "fetchImage", void 0);
__decorate([
    property({
        attribute: 'person-presence',
        type: Object
    })
], MgtPerson.prototype, "personPresence", null);
__decorate([
    property({
        attribute: 'person-card',
        converter: (value, type) => {
            value = value.toLowerCase();
            if (typeof PersonCardInteraction[value] === 'undefined') {
                return PersonCardInteraction.none;
            }
            else {
                return PersonCardInteraction[value];
            }
        }
    })
], MgtPerson.prototype, "personCardInteraction", void 0);
__decorate([
    property({ attribute: 'line1-property' })
], MgtPerson.prototype, "line1Property", void 0);
__decorate([
    property({ attribute: 'line2-property' })
], MgtPerson.prototype, "line2Property", void 0);
__decorate([
    property({
        converter: value => {
            if (!value || value.length === 0) {
                return PersonViewType.avatar;
            }
            value = value.toLowerCase();
            if (typeof PersonViewType[value] === 'undefined') {
                return PersonViewType.avatar;
            }
            else {
                return PersonViewType[value];
            }
        }
    })
], MgtPerson.prototype, "view", void 0);
__decorate([
    internalProperty()
], MgtPerson.prototype, "_personCardShouldRender", void 0);
__decorate([
    internalProperty()
], MgtPerson.prototype, "_fetchedPresence", void 0);
__decorate([
    internalProperty()
], MgtPerson.prototype, "_fetchedImage", void 0);
MgtPerson = __decorate([
    customElement('mgt-person')
], MgtPerson);
export { MgtPerson };
//# sourceMappingURL=mgt-person.js.map